import React, { useContext } from 'react';

import DataGrid from '@gogame/data-grid';
import { Stack, Typography, Tags } from '@gogame/components';

import { PreviewFile } from 'scenes/Gallery/components/File';
import {
  AttributeList,
  AttributeItem,
} from 'scenes/Gallery/components/AttributeList';

import ConversionGroupPreviewScreenLink from 'scenes/Gallery/ConversionPages/ConversionGroups/ConversionGroupPreviewScreenLink';

import { BatchToolbarDataGrid } from './components/BatchToolbar';
import Toolbar from './components/Toolbar';
import ConversionGroupsDataTableContext from './ConversionGroupsDataTableContext';
import {
  COLUMN_ID,
  COLUMN_PREVIEW,
  COLUMN_NAME,
  COLUMN_APPLICATION,
  COLUMN_DOMAIN,
  COLUMN_LOCALES,
  COLUMN_FLOW,
  COLUMN_TAGS,
} from './constants';

const ConversionGroupsDataTable = ({ height, tabs }) => {
  const {
    data,
    isLoading,
    isRefetching,
    tableSettings,
    columns,
    onReorder,
    onCollapse,
    onChangeSettings,
  } = useContext(ConversionGroupsDataTableContext);

  return (
    <DataGrid
      rows={data?.rows}
      count={data?.count}
      loading={isLoading}
      fetching={isRefetching}
      onChangeSettings={onChangeSettings}
      onReorder={onReorder}
      onCollapse={onCollapse}
      tableSettings={tableSettings}
      columnsState={columns}
      checkboxSelection={true}
      toolbar={<Toolbar tabs={tabs} />}
      selectedRowsToolbarActions={({ selectedValues }) => (
        <BatchToolbarDataGrid selectedValues={selectedValues} />
      )}
      storageKey={'conversion-groups'}
      defaultColumnWidth={140}
      rowHeight={80}
      minHeight={height}
      notInitedLabel={'No records'}
    >
      <DataGrid.Column
        field={COLUMN_ID}
        type={'text'}
        headerName={'ID'}
        width={90}
        fixed={true}
        resizable={false}
        align={'right'}
        headerAlign={'right'}
      />
      <DataGrid.Column
        field={COLUMN_PREVIEW}
        headerName={'Preview'}
        width={130}
        fixed={true}
        resizable={false}
        sortable={false}
        formatter={(value, { [COLUMN_ID]: id }) => (
          <ConversionGroupPreviewScreenLink id={id}>
            <PreviewFile
              src={value}
              width={109}
              height={64}
              heightCover={true}
            />
          </ConversionGroupPreviewScreenLink>
        )}
      />
      <DataGrid.Column
        field={COLUMN_NAME}
        headerName={'Name'}
        width={460}
        formatter={(value, { [COLUMN_ID]: id, conversionPageCount, isNew }) => (
          <Stack spacing={0.5} zeroMinWidth={true}>
            <Stack spacing={0.5} direction={'row'} alignItems={'center'}>
              <Typography variant={'body2'} noWrap={true}>
                <ConversionGroupPreviewScreenLink id={id}>
                  {value}
                </ConversionGroupPreviewScreenLink>
              </Typography>
              {isNew ? (
                <AttributeItem type={'icon'} valueParser={'new'} />
              ) : null}
            </Stack>
            <AttributeList type={'text'}>
              <AttributeItem
                valueParser={'conversionPagesCount'}
                value={conversionPageCount}
              />
            </AttributeList>
          </Stack>
        )}
      />
      <DataGrid.Column
        field={COLUMN_APPLICATION}
        headerName={'Application'}
        getValue={(v) => v?.name}
      />
      <DataGrid.Column field={COLUMN_DOMAIN} headerName={'Domain'} />
      <DataGrid.Column
        field={COLUMN_LOCALES}
        headerName={'Locales'}
        type={'list'}
        width={112}
        sortable={false}
        getItemValue={(v) => v}
        limit={1}
      />
      <DataGrid.Column
        field={COLUMN_FLOW}
        headerName={'Flow'}
        width={100}
        getValue={(v) => v?.name}
      />
      <DataGrid.Column
        field={COLUMN_TAGS}
        headerName={'Tags'}
        width={260}
        sortable={false}
        renderCell={({ value, colDef: { width } }) => {
          return value?.length ? (
            <Stack
              direction={'row'}
              alignItems={'center'}
              height={'100%'}
              lineHeight={'normal'}
            >
              <Tags
                data={value}
                getTagLabel={({ name }) => name}
                width={width}
                lineClamp={2}
              />
            </Stack>
          ) : (
            '-'
          );
        }}
      />
    </DataGrid>
  );
};
export default ConversionGroupsDataTable;
